import { Flex } from '@chakra-ui/react';
import { NAVBAR_HEIGHT } from 'constants/editor';
import { S3_USER_MEDIA_BUCKET_LINK } from 'constants/imageUpload';
import { IMAGE_UPLOADED } from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { EDITOR_REPLACE_MENU_TABLIST_ID } from 'modules/Editor/Editor.config';
import { RootState, getInitialWidgetPosition, useAppDispatch, useAppSelector, useAppStore } from 'modules/Editor/store';
import { addNewWidget } from 'modules/Editor/store';
import {
  selectActiveWidgetMenu,
  selectIsUploading,
  selectReplaceWidgetMenuIndex,
} from 'modules/Editor/store/editorSettingsSelector';
import {
  setActiveWidgetMenu,
  setIsUploading as setIsFileUploading,
  setReplaceWidgetMenuIndex,
} from 'modules/Editor/store/editorSettingsSlice';
import { ReplaceMenuTabIndex } from 'modules/Editor/store/editorSettingsSlice.types';
import { selectActiveWidgets } from 'modules/Editor/store/widgetSelector';
import { GET_USER_MEDIA_API } from 'modules/User';
import { useImageUpload } from 'modules/User/useImageUpload';
import { useSWRConfig } from 'swr';
import { WIDGET_MENU_OPTIONS } from 'types/WidgetMenu.types';
import { loadImageData } from 'utils/image';
import { generateDefaultData as generateImageWidgetData } from 'widgets/ImageWidget/ImageWidget.helpers';

/*
 * The main area of the editor (wraps everything excluding the navbar)
 * Has file drop for uploading images
 */
export const MainContainer = ({ children }: { children: React.ReactNode }) => {
  const isUploading = useAppSelector(selectIsUploading);
  const dispatch = useAppDispatch();
  const { mutate } = useSWRConfig();
  const store = useAppStore();

  const { hasValidUploadLimits, filterOutInvalidFiles, uploadImageFile, showUploadSuccessToast } = useImageUpload({
    isUploading,
  });

  const setIsUploading = (isUploading: boolean) => dispatch(setIsFileUploading(isUploading));

  const handleFileDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!e.dataTransfer.files) return;

    const files = Array.from(e.dataTransfer.files) as File[];

    const isValidUploadLimits = hasValidUploadLimits(files);
    if (!isValidUploadLimits) return;

    // if the replace menu is open - don't add the images as widgets to the page
    const state = store.getState() as RootState;
    const activeWidgetMenu = selectActiveWidgetMenu(state);
    const isReplaceMenuActive = activeWidgetMenu === WIDGET_MENU_OPTIONS.REPLACE;

    if (isReplaceMenuActive) {
      const activeWidgets = selectActiveWidgets(state);
      // does not support image upload
      if (activeWidgets[0].responsiveGroupId) return;
    }

    setIsUploading(true);

    const filteredFiles = filterOutInvalidFiles(files);
    const uploadFilePromises = filteredFiles.map((file) => uploadImageFile(file));

    if (uploadFilePromises.length > 0) {
      await Promise.all(uploadFilePromises);

      // add the uploaded images as widgets
      const widgetList = [];

      // if multiple images are uploaded so they are not stacked on the same position
      let topAdjustment = 0;
      let leftAdjustment = 0;
      const offset = 10;

      for (const filePromise of uploadFilePromises) {
        const file = await filePromise;

        if (!file) continue;
        if (!file.file_id) continue;
        if (isReplaceMenuActive) continue;

        const { file_id: fileId } = file;

        // use the file id to generate the image url
        const url = `${S3_USER_MEDIA_BUCKET_LINK}${fileId}`;

        // get the image dimensions of the actual image
        // the image/height from the response are not reliable for the image dimensions
        let width = 0;
        let height = 0;

        try {
          const { naturalWidth, naturalHeight } = await loadImageData(url);
          width = naturalWidth;
          height = naturalHeight;
        } catch (e) {
          // should not get here but just in case
          console.error(e);
          continue;
        }

        const { top, left, infographWidth, infographHeight } = getInitialWidgetPosition();
        const widget = generateImageWidgetData(
          url,
          { width, height },
          { width: infographWidth, height: infographHeight },
          top + topAdjustment,
          left + leftAdjustment,
        );

        topAdjustment += offset;
        leftAdjustment += offset;

        widgetList.push(widget);
      }

      // add the uploaded images as one batch
      if (widgetList.length > 0) dispatch(addNewWidget(widgetList));

      // refetch the user media
      mutate(GET_USER_MEDIA_API);
      const uploadedImageCount = uploadFilePromises.length;
      showUploadSuccessToast(uploadedImageCount);
      Mixpanel.track(IMAGE_UPLOADED, { image_count: uploadedImageCount });
    }

    setIsUploading(false);
  };

  const handleOnDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const state = store.getState() as RootState;
    const activeWidgetMenu = selectActiveWidgetMenu(state);

    // if a non-file is dragged the upload menu should not open
    if (
      (e?.dataTransfer.types?.length > 0 && e?.dataTransfer?.types?.[0] !== 'Files') ||
      !e?.dataTransfer.types.length
    ) {
      return;
    }

    // if the file uploads menu is not open and replace is not open -> open it
    if (activeWidgetMenu !== WIDGET_MENU_OPTIONS.IMAGES && activeWidgetMenu !== WIDGET_MENU_OPTIONS.REPLACE) {
      dispatch(setActiveWidgetMenu(WIDGET_MENU_OPTIONS.IMAGES));
      return;
    }

    // if the active widget menu is replace -> open the upload tab if its not open
    if (activeWidgetMenu === WIDGET_MENU_OPTIONS.REPLACE) {
      // does not support image upload
      const activeWidgets = selectActiveWidgets(state);
      if (activeWidgets[0].responsiveGroupId) return;

      const tabIndex = selectReplaceWidgetMenuIndex(state);
      if (tabIndex !== ReplaceMenuTabIndex.UPLOAD) {
        dispatch(setReplaceWidgetMenuIndex(ReplaceMenuTabIndex.UPLOAD));

        // need to focus the upload tab - else the internal focus handling can reset the tab index after the drop event
        const tabEl = document.getElementById(EDITOR_REPLACE_MENU_TABLIST_ID)?.children?.[ReplaceMenuTabIndex.UPLOAD];
        if (tabEl) (tabEl as HTMLElement).focus();
      }
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  return (
    <Flex
      as='main'
      minH={`calc(100vh - ${NAVBAR_HEIGHT}px)`}
      onDrop={handleFileDrop}
      onDragLeave={handleDragLeave}
      onDragOver={handleOnDragOver}
    >
      {children}
    </Flex>
  );
};
